import { LazyBlockComponent } from "@/types/blocks";
import { lazy } from "react";

// --- HERO ---
const HeroSplitRight = lazy(() => import("./blocks/hero/HeroSplitRight"));
const HeroCollageRight = lazy(() => import("./blocks/hero/HeroCollageRight"));
const HeroCenteredImageBottom = lazy(
  () => import("./blocks/hero/HeroCenteredImageBottom"),
);
const HeroFeatureAccordion = lazy(
  () => import("./blocks/hero/HeroFeatureAccordion"),
);
const PricingHero = lazy(() => import("./blocks/hero/PricingHero"));
const BlogHero = lazy(() => import("./blocks/hero/BlogHero"));
const ClientsHero = lazy(() => import("./blocks/hero/ClientsHero"));

// --- FEATURES ---
const FeaturesCarouselInteractive = lazy(
  () => import("./blocks/features/FeaturesCarouselInteractive"),
);
const FeaturesVerticalImageTabs = lazy(
  () => import("./blocks/features/FeaturesVerticalImageTabs"),
);
const FeaturesGridIcons = lazy(
  () => import("./blocks/features/FeaturesGridIcons"),
);
const FeaturesAccordionImageLeft = lazy(
  () => import("./blocks/features/FeaturesAccordionImageLeft"),
);
const FeaturesGridScreenshots = lazy(
  () => import("./blocks/features/FeaturesGridScreenshots"),
);
const FeaturesCarouselIcons = lazy(
  () => import("./blocks/features/FeaturesCarouselIcons"),
);

// --- SPOTLIGHT ---
const SpotlightListZigZag = lazy(
  () => import("./blocks/spotlight/SpotlightListZigZag"),
);

// --- STEPS ---
const StepsProcessConnected = lazy(
  () => import("./blocks/steps/StepsProcessConnected"),
);

// --- CTA ---
const CtaSimpleCenter = lazy(() => import("./blocks/cta/CtaSimpleCenter"));
const CTASimpleSplit = lazy(() => import("./blocks/cta/CTASimpleSplit"));
const CtaNewsletterSignup = lazy(
  () => import("./blocks/cta/CtaNewsletterSignup"),
);

// --- TESTIMONIAL ---
const TestimonialCarousel = lazy(
  () => import("./blocks/testimonial/TestimonialCarousel"),
);

// --- PRICING ---
const PricingCards = lazy(() => import("./blocks/pricing/PricingCards"));

// --- TEAM ---
const TeamGridCards = lazy(() => import("./blocks/team/TeamGridCards"));

// --- TEXT/CONTENT ---
const SpotlightStorySplit = lazy(
  () => import("./blocks/text-content/SpotlightStorySplit"),
);

// --- MARKETING ---
const LogosSimpleRow = lazy(() => import("./blocks/marketing/LogosSimpleRow"));
const ClientsLogoGridPagination = lazy(
  () => import("./blocks/marketing/ClientsLogoGridPagination"),
);
const SocialProofBar = lazy(() => import("./blocks/marketing/SocialProofBar"));

// --- FAQ ---
const FAQAccordionContact = lazy(
  () => import("./blocks/faq/FAQAccordionContact"),
);

// --- FORM ---
const ContactFormMailbox = lazy(
  () => import("./blocks/form/ContactFormMailbox"),
);
const OfficeContactDetails = lazy(
  () => import("./blocks/form/OfficeContactDetails"),
);
const ContactFormSplit = lazy(
  () => import("./blocks/form/ContactFormSplit"),
);

// --- LEGAL ---
const TermsOfService = lazy(() => import("./blocks/legal/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./blocks/legal/PrivacyPolicy"));

// --- DEFAULTS ---
const MissingBlock = lazy(() => import("./blocks/defaults/MissingBlock"));

const BLOCK_REGISTRY: Record<string, LazyBlockComponent> = {
  // Hero
  hero_split_right: HeroSplitRight,
  hero_collage_right: HeroCollageRight,
  hero_centered_image_bottom: HeroCenteredImageBottom,
  hero_feature_accordion: HeroFeatureAccordion,
  pricing_hero: PricingHero,
  blog_hero: BlogHero,
  clients_hero: ClientsHero,

  // Features
  features_carousel_interactive: FeaturesCarouselInteractive,
  features_vertical_image_tabs: FeaturesVerticalImageTabs,
  features_grid_icons: FeaturesGridIcons,
  features_accordion_image_left: FeaturesAccordionImageLeft,
  features_grid_screenshots: FeaturesGridScreenshots,
  features_carousel_icons: FeaturesCarouselIcons,

  // Spotlight
  spotlight_list_zigzag: SpotlightListZigZag,

  // Steps
  steps_process_connected: StepsProcessConnected,

  // CTA
  cta_simple_center: CtaSimpleCenter,
  cta_simple_split: CTASimpleSplit,
  cta_newsletter_signup: CtaNewsletterSignup,

  // Testimonial
  testimonial_carousel: TestimonialCarousel,

  // Pricing
  pricing_cards: PricingCards,

  // Team
  team_grid_cards: TeamGridCards,

  // Text/Content
  spotlight_story_split: SpotlightStorySplit,

  // Marketing
  logos_simple_row: LogosSimpleRow,
  clients_logo_grid_pagination: ClientsLogoGridPagination,
  social_proof_bar: SocialProofBar,

  // FAQ
  faq_accordion_contact: FAQAccordionContact,

  // Form
  contact_form_mailbox: ContactFormMailbox,
  office_contact_details: OfficeContactDetails,
  contact_form_split: ContactFormSplit,

  // Legal
  terms_of_service: TermsOfService,
  privacy_policy: PrivacyPolicy,

  // System Defaults
  default: MissingBlock,
};

export const getBlockComponent = (renderKey: string) => {
  if (!renderKey) return MissingBlock;

  const Component = BLOCK_REGISTRY[renderKey];

  if (!Component) {
    console.warn(
      `[CMS Registry] No component found for render key: "${renderKey}"`,
    );
    return MissingBlock;
  }

  return Component;
};
